import React, { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import Sidebar from './Sidebar';
import Header from './Header';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronUp, X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import ChatWidget from './ChatWidget';
import apiService from '../../services/api';
import { JobQueueProvider } from '../../contexts/Jobqueuecontext.js';
import JobNotificationPanel from '../common/Jobnotificationpanel';

// ============================================================================
// TOAST CONTEXT & PROVIDER
// ============================================================================

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within Layout component');
  }
  return context;
};

const Toast = ({ id, message, type = 'info', duration = 3000, onClose }) => {
  const icons = {
    success: <CheckCircle className="h-5 w-5" />,
    error: <AlertCircle className="h-5 w-5" />,
    warning: <AlertTriangle className="h-5 w-5" />,
    info: <Info className="h-5 w-5" />
  };

  const styles = {
    success: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200',
    error: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200',
    warning: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200',
    info: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'
  };

  const iconColors = {
    success: 'text-emerald-600 dark:text-emerald-400',
    error: 'text-red-600 dark:text-red-400',
    warning: 'text-amber-600 dark:text-amber-400',
    info: 'text-blue-600 dark:text-blue-400'
  };

  return (
    <div
      className={`
        flex items-center gap-3 px-4 py-3 rounded-xl border-2 shadow-xl
        ${styles[type]}
        animate-in slide-in-from-right duration-300
        backdrop-blur-sm
        min-w-[300px] max-w-md
      `}
    >
      <div className={iconColors[type]}>
        {icons[type]}
      </div>
      <p className="flex-1 text-sm font-medium">
        {message}
      </p>
      <button
        onClick={() => onClose(id)}
        className="p-1 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// ============================================================================
// MODAL CONTEXT & PROVIDER
// ============================================================================

const ModalContext = createContext();

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within Layout component');
  }
  return context;
};

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  closeOnBackdrop = true,
  showCloseButton = true,
  triggerRef = null,
  position = 'center',
  className = ''
}) => {
  const modalRef = useRef(null);
  const [modalPosition, setModalPosition] = useState({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
  const [mounted, setMounted] = useState(false);

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    full: 'max-w-7xl mx-4'
  };

  useEffect(() => {
    if (isOpen && triggerRef?.current && position === 'near-trigger') {
      const calculatePosition = () => {
        const trigger = triggerRef.current;
        const modal = modalRef.current;
        
        if (!trigger || !modal) return;

        const triggerRect = trigger.getBoundingClientRect();
        const modalRect = modal.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        let top = triggerRect.bottom + 10;
        let left = triggerRect.left;

        if (top + modalRect.height > viewportHeight) {
          const topPosition = triggerRect.top - modalRect.height - 10;
          if (topPosition > 0) {
            top = topPosition;
          } else {
            top = (viewportHeight - modalRect.height) / 2;
          }
        }

        if (left + modalRect.width > viewportWidth) {
          left = viewportWidth - modalRect.width - 20;
        }
        if (left < 20) {
          left = 20;
        }

        setModalPosition({
          top: `${top}px`,
          left: `${left}px`,
          transform: 'none'
        });
      };

      setTimeout(calculatePosition, 0);
      window.addEventListener('resize', calculatePosition);
      return () => window.removeEventListener('resize', calculatePosition);
    } else if (isOpen && position === 'top') {
      setModalPosition({
        top: '10vh',
        left: '50%',
        transform: 'translateX(-50%)'
      });
    } else {
      setModalPosition({
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      });
    }
  }, [isOpen, triggerRef, position]);

  useEffect(() => {
    if (isOpen) {
      const mainContent = document.getElementById('main-content');
      const originalOverflow = mainContent ? mainContent.style.overflow : '';
      
      if (mainContent) {
        mainContent.style.overflow = 'hidden';
      }
      
      setMounted(true);

      return () => {
        if (mainContent) {
          mainContent.style.overflow = originalOverflow;
        }
      };
    } else {
      setMounted(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 ${mounted ? 'animate-in fade-in duration-200' : ''}`}
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={closeOnBackdrop ? onClose : undefined}
        style={{ position: 'fixed' }}
      />

      <div
        ref={modalRef}
        className={`
          relative w-full ${sizeClasses[size]}
          bg-white dark:bg-slate-900
          rounded-2xl shadow-2xl
          max-h-[90vh] overflow-hidden
          animate-in zoom-in-95 duration-200
          ${className}
        `}
        style={{
          position: 'fixed',
          ...modalPosition,
          zIndex: 10000
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-800/50">
            {title && (
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="ml-auto p-2 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors group"
                aria-label="Close modal"
              >
                <X className="h-5 w-5 text-slate-500 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-200" />
              </button>
            )}
          </div>
        )}

        <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

// ============================================================================
// MAIN LAYOUT COMPONENT
// ============================================================================

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [toasts, setToasts] = useState([]);
  const { getBrandColors } = useAuth();
  const { userType, user } = useAuth();
  const brandColors = getBrandColors();
  const location = useLocation();
  const mainContentRef = useRef(null);
const [canUseChatWidget, setCanUseChatWidget] = useState(false);
// console.log("userType,user",userType,user)
useEffect(() => {
  const loadPermission = async () => {
    try {
      const res = await apiService.getClientLicense();
      const allowed = res?.data?.activeLicense?.search_types_allowed || [];

const isLicenseAllowed = allowed.includes("Agent_Live_Chat_Permission");

let hasPermission = false;

if (userType === 'client') {
  hasPermission = isLicenseAllowed;
} else if (userType === 'client_child_user') {
  const searchTypes = user?.search_types_allowed || [];

  hasPermission =
    isLicenseAllowed &&
    searchTypes.includes("Agent_Live_Chat_Permission");
}

setCanUseChatWidget(hasPermission);

setCanUseChatWidget(hasPermission);
    } catch (e) {
      setCanUseChatWidget(false);
    }
  };

  loadPermission();
}, []);
  // Toast functions
  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type, duration }]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const toastFunctions = {
    success: useCallback((message, duration) => addToast(message, 'success', duration), [addToast]),
    error: useCallback((message, duration) => addToast(message, 'error', duration), [addToast]),
    warning: useCallback((message, duration) => addToast(message, 'warning', duration), [addToast]),
    info: useCallback((message, duration) => addToast(message, 'info', duration), [addToast]),
    addToast,
    removeToast
  };

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Scroll tracking
  useEffect(() => {
    const mainContent = mainContentRef.current;
    if (!mainContent) return;

    const handleScroll = () => {
      const scrollTop = mainContent.scrollTop;
      const scrollHeight = mainContent.scrollHeight - mainContent.clientHeight;
      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;

      setScrollProgress(progress);
      setShowScrollTop(scrollTop > 300);
    };

    mainContent.addEventListener('scroll', handleScroll);
    return () => mainContent.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <ToastContext.Provider value={toastFunctions}>
      <JobQueueProvider>
        <div
        className="h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50"
        style={{
          '--brand-primary': brandColors.primary,
          '--brand-accent': brandColors.accent,
        }}
      >
        {/* Animated Background Pattern */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden opacity-20 dark:opacity-10">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-slate-300 dark:bg-slate-800 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-slate-400 dark:bg-slate-700 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-slate-300 dark:bg-slate-800 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        {/* Scroll Progress Bar */}
        <div className={`fixed top-16 left-0 right-0 h-0.5 bg-slate-200/50 dark:bg-slate-800/50 z-40 ${isMobile ? '' : 'lg:left-72'}`}>
          <div
            className="h-full bg-gradient-to-r from-slate-500 via-slate-600 to-slate-700 transition-all duration-300 ease-out shadow-sm"
            style={{ width: `${scrollProgress}%` }}
          ></div>
        </div>

        {/* SIDEBAR */}
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          brandColors={brandColors}
          isMobile={isMobile}
        />

        {/* HEADER */}
        <Header
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          brandColors={brandColors}
          isMobile={isMobile}
        />

        {/* MOBILE OVERLAY */}
        {sidebarOpen && isMobile && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 animate-in fade-in duration-200"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* MAIN CONTENT */}
        <main
          ref={mainContentRef}
          id="main-content"
          className={`
            fixed
            top-16
            right-0
            bottom-0
            overflow-y-auto
            overflow-x-hidden
            scroll-smooth
            transition-all duration-300 ease-out
            ${isMobile ? 'left-0' : 'left-72'}
          `}
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgb(148 163 184) transparent',
          }}
        >
          <div className="relative min-h-full w-full">
            <div className="relative w-full min-h-full bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
              <div className="w-full h-full">
                <Outlet />
              </div>
            </div>
          </div>
        </main>

        {/* Toast Container */}
        {toasts.length > 0 && createPortal(
          <div 
            className="fixed top-20 right-4 z-[99999] flex flex-col gap-3 pointer-events-none"
            style={{
              position: 'fixed',
              top: '5rem',
              right: '1rem',
              zIndex: 99999
            }}
          >
            {toasts.map((toast) => (
              <div key={toast.id} className="pointer-events-auto">
                <Toast {...toast} onClose={removeToast} />
              </div>
            ))}
          </div>,
          document.body
        )}

        {/* Legal Drafting Job Notification Panel */}
        <JobNotificationPanel />

        {/* Chat Support Widget */}
{canUseChatWidget && <ChatWidget />}
        {/* Scroll to Top Button */}
        <button
          onClick={scrollToTop}
          className={`
            fixed z-50
            w-12 h-12 rounded-2xl
            bg-gradient-to-br from-slate-600 to-slate-700
            hover:from-slate-700 hover:to-slate-800
            text-white
            shadow-xl shadow-slate-500/30
            hover:shadow-2xl hover:shadow-slate-500/40
            hover:scale-110
            active:scale-95
            transition-all duration-300 ease-out
            group
            ${showScrollTop ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0 pointer-events-none'}
            ${isMobile ? 'bottom-6 right-6' : 'bottom-8 right-8'}
          `}
          aria-label="Scroll to top"
        >
          <div className="absolute inset-0 rounded-2xl bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          <ChevronUp className="h-6 w-6 mx-auto transition-transform duration-300 group-hover:-translate-y-0.5" />
          
          <div className="absolute inset-0 rounded-2xl">
            <div className="absolute inset-0 rounded-2xl bg-slate-400 opacity-0 group-hover:opacity-20 group-hover:scale-150 transition-all duration-500"></div>
          </div>
        </button>

        {/* Custom Styles */}
        <style jsx>{`
          #main-content::-webkit-scrollbar {
            width: 10px;
          }

          #main-content::-webkit-scrollbar-track {
            background: transparent;
          }

          #main-content::-webkit-scrollbar-thumb {
            background: rgb(148 163 184);
            border-radius: 5px;
            border: 2px solid transparent;
            background-clip: padding-box;
          }

          #main-content::-webkit-scrollbar-thumb:hover {
            background: rgb(100 116 139);
            background-clip: padding-box;
          }

          .dark #main-content::-webkit-scrollbar-thumb {
            background: rgb(71 85 105);
            background-clip: padding-box;
          }

          .dark #main-content::-webkit-scrollbar-thumb:hover {
            background: rgb(51 65 85);
            background-clip: padding-box;
          }

          @keyframes blob {
            0% {
              transform: translate(0px, 0px) scale(1);
            }
            33% {
              transform: translate(20px, -30px) scale(1.05);
            }
            66% {
              transform: translate(-15px, 15px) scale(0.95);
            }
            100% {
              transform: translate(0px, 0px) scale(1);
            }
          }

          .animate-blob {
            animation: blob 10s infinite ease-in-out;
          }

          .animation-delay-2000 {
            animation-delay: 2s;
          }

          .animation-delay-4000 {
            animation-delay: 4s;
          }

          @keyframes fade-in {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes slide-in-from-right {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }

          @keyframes zoom-in-95 {
            from {
              transform: scale(0.95);
              opacity: 0;
            }
            to {
              transform: scale(1);
              opacity: 1;
            }
          }

          .animate-in {
            animation: fade-in 0.2s ease-out;
          }

          .fade-in {
            animation: fade-in 0.2s ease-out;
          }

          .slide-in-from-right {
            animation: slide-in-from-right 0.3s ease-out;
          }

          .zoom-in-95 {
            animation: zoom-in-95 0.2s ease-out;
          }

          *:focus-visible {
            outline: 2px solid rgb(100 116 139);
            outline-offset: 2px;
          }

          [role="dialog"],
          [data-modal],
          .modal-backdrop {
            position: fixed !important;
          }
        `}</style>
      </div>
      </JobQueueProvider>
    </ToastContext.Provider>
  );
};

// Export Modal component for use in other components
export { Modal };
export default Layout;